<template>
  <div v-for="({ count, rating, percentage }, index) in countPerRating" :key="index" class="rating-block" :data-testid="starRatingSnapshotBlock">
    <button
      class="rating-value"
      :class="{ 'link-body': count }"
      :data-testid="starRatingSnapshotBlockValue"
      :data-testvalue="rating"
      :aria-label="getAriaLabel(rating, percentage, count)"
      @click="onRatingClick(rating, count)"
    >
      {{ rating }} {{ isMcom ? 'S' : 's' }}tar{{ rating > 1 ? 's' : '' }}
    </button>
    <div class="text-center rating-percentage" :data-testid="starRatingSnapshotBlockVisualizer">
      <StarCountVisualizer :value="percentage" />
    </div>
    <div class="text-right body rating-count" :data-testid="starRatingSnapshotBlockCount">
      {{ count }}
    </div>
  </div>
</template>

<script setup>
import { useBrand } from '~/composables/useBrand';
import useReviews from '~/modules/reviews/composables/useReviews';
import useReviewsToggle from '~/modules/reviews/composables/useReviewsToggle';
import { useProduct } from '~/modules/product/composables/useProduct';
import { trackEvent } from '~/utils/bvPixel';
import { isNullable } from '~/utils/commonUtils';
import StarCountVisualizer from './StarCountVisualizer';
import {
  starRatingSnapshotBlock,
  starRatingSnapshotBlockCount,
  starRatingSnapshotBlockValue,
  starRatingSnapshotBlockVisualizer,
} from '../../constants/testIds';

defineProps({
  countPerRating: {
    /** @type import('vue').PropType<import('~/modules/reviews/types').CountPerRating[]> */
    type: Array,
    required: true,
  },
});

const getPluralizedStr = (str, num) => `${str}${num > 1 ? 's' : ''}`;

const getPercentageLabel = (percentage) => {
  if (isNullable(percentage)) return '';
  return `${percentage.toFixed(1)}% of reviews`;
};
const getCountTotalLabel = (count, capitalize = false, showZero = false) => {
  if (isNullable(count) || (count === 0 && !showZero)) return '';
  const article = capitalize ? 'A' : 'a';
  return `${article} total of ${count} ${getPluralizedStr('review', count)}`;
};

const connectText = (...textArgs) => textArgs.filter(Boolean).join(' and ');

const getAriaLabel = (rating, percentage, count) => {
  const percentageText = getPercentageLabel(percentage);
  const countText = getCountTotalLabel(count, !percentage, isNullable(percentage));
  return `${connectText(percentageText, countText)} have ${rating} ${getPluralizedStr('star', rating)}`;
};

const { productId } = useProduct();

const { isMcom } = useBrand();
const { handleFilterByChange } = useReviews();
const { expandReviews } = useReviewsToggle();
/**
 * @param {number} rating
 * @param {number} count
 */
function onRatingClick(rating, count) {
  if (!count) return;
  expandReviews();
  handleFilterByChange(String(rating));

  requestAnimationFrame(() => {
    const firstReview = document.querySelector('#review-section .review-item');
    if (firstReview) {
      const firstInteractive = firstReview.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');

      if (firstInteractive) {
        firstInteractive.focus();
      }
    }
  });

  // BV track for stats - these run when user clicks on a star rating from top histogram
  trackEvent('filter', productId, 'star_snapshot', String(rating));
}
</script>

<style scoped lang="scss">
.rating-block {
  display: flex;
  height: rem-calc(20);
}

.rating-value,
.rating-count {
  width: rem-calc(48);
}

.rating-percentage {
  flex-grow: 1;
  padding: rem-calc(4) rem-calc(8);
}
</style>
